// 移植自 crm/src/components/Toast.jsx。addToast 是普通函式，非元件（攔截器、mutation 預設錯誤處理）也能呼叫。
import { useEffect, useRef, useSyncExternalStore } from 'react'
import { createPortal } from 'react-dom'
import { IoCheckmarkCircle, IoCloseCircle, IoWarning, IoInformationCircle } from '@/components/ui/ionicons'
import { CloseIcon } from './icons'

let messages = []
const listeners = new Set()

function setMessages(next) {
  messages = next
  listeners.forEach((fn) => fn())
}

function subscribe(fn) {
  listeners.add(fn)
  return () => listeners.delete(fn)
}

export function addToast({ level = 'success', title, message, timeout = 3000 }) {
  const msg = { id: crypto.randomUUID(), level, title, message, removing: false }
  setMessages([...messages, msg].slice(-10))
  setTimeout(() => removeToast(msg.id), timeout)
}

function removeToast(id) {
  setMessages(messages.map((m) => (m.id === id ? { ...m, removing: true } : m)))
  // 與 src/index.css 的 .toast-exit 動畫時間同步
  setTimeout(() => setMessages(messages.filter((m) => m.id !== id)), 300)
}

const ICON_CLS = 'shrink-0 size-6'

function LevelIcon({ level }) {
  if (level === 'success') return <IoCheckmarkCircle className={`${ICON_CLS} text-brand-primary`} />
  if (level === 'error') return <IoCloseCircle className={`${ICON_CLS} text-error`} />
  if (level === 'warning') return <IoWarning className={`${ICON_CLS} text-warning`} />
  return <IoInformationCircle className={`${ICON_CLS} text-brand-blue-dark-2`} />
}

export function Toaster() {
  const list = useSyncExternalStore(subscribe, () => messages)
  const ref = useRef(null)

  // 同舊版：掛載時就 show 進 top layer
  useEffect(() => {
    ref.current?.showPopover?.()
  }, [])

  // 同舊版：訊息有任何變動就 hide 再 show，確保壓在之後開的 dialog 之上
  // （只看則數的話，滿 10 則後新增一則長度不變，就不會重新浮上來）
  useEffect(() => {
    const el = ref.current
    if (!el?.showPopover || list.length === 0) return
    if (el.matches(':popover-open')) el.hidePopover()
    const raf = requestAnimationFrame(() => el.showPopover())
    return () => cancelAnimationFrame(raf)
  }, [list])

  return createPortal(
    <div ref={ref} popover="manual" className="toast-container">
      {list.map((e) => (
        <div key={e.id} className={`toast-alert relative bg-base-100 ${e.removing ? 'toast-exit' : 'toast-enter'}`}>
          <LevelIcon level={e.level} />
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            {e.title && <span className="break-words text-base font-bold leading-6 text-brand-black-1000">{e.title}</span>}
            {e.message && (
              <span className={`break-words leading-5 text-sm ${e.title ? 'text-brand-black-700' : 'text-brand-black-1000'}`}>{e.message}</span>
            )}
          </div>
          <button
            type="button"
            className="absolute right-3 top-3 flex size-5 items-center justify-center text-brand-black-400 transition-colors hover:text-brand-black-1000"
            onClick={() => removeToast(e.id)}
          >
            <CloseIcon className="size-[11px]" />
          </button>
        </div>
      ))}
    </div>,
    document.body,
  )
}
